/**
 * Combined admin view for BE-1844 silent-failure detection: the per-room
 * capture timeline for a chosen day (are rooms actually recording?) alongside
 * the appointments the capture judge flagged as suspect-uncaptured, missing
 * presence entirely, or incomplete.
 */
import { parseDateParam, shiftDays, toISODate } from "@calendar/weekDates";
import { CaptureNodesList } from "@capture/CaptureNodesList";
import { CaptureTimeline } from "@capture/CaptureTimeline";
import { FlaggedVisitsList } from "@capture/FlaggedVisitsList";
import { useClinic } from "@clinic/clinicQueries";
import { AsyncBoundary } from "@components/AsyncBoundary";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  PanelError,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { useNow } from "@utils/use-now";
import { format } from "date-fns";
import { useState } from "react";

/**
 * Today's `yyyy-MM-dd` in the given IANA timezone (browser-local when the
 * timezone is missing/invalid). The timeline endpoint interprets its `date`
 * param as a clinic-local calendar day, so "today" must be computed in the
 * clinic's timezone - a viewer in another timezone would otherwise request
 * yesterday's/tomorrow's window near day boundaries.
 */
const todayKeyIn = (timeZone: string | undefined, epochMs: number): string => {
  try {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(epochMs);
  } catch {
    return toISODate(new Date(epochMs));
  }
};

export const CapturePanel = () => {
  const { data: clinic } = useClinic();
  // Day selection is an offset from clinic-today rather than an absolute
  // date: offset 0 tracks clinic-today even across midnight or while the
  // clinic (and its timezone) is still loading. `useNow` keeps this panel
  // re-rendering (shared 30s tick) so the key actually rolls over at the
  // clinic's midnight - data refetches alone only re-render the children.
  const now = useNow();
  const [dayOffset, setDayOffset] = useState(0);
  const todayKey = todayKeyIn(clinic?.timezone, now);
  const dateKey =
    dayOffset === 0
      ? todayKey
      : toISODate(shiftDays(parseDateParam(todayKey), dayOffset));
  const isToday = dayOffset === 0;
  const date = parseDateParam(dateKey);

  return (
    <div className="flex flex-col gap-4 font-body">
      <section className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-annotation-caps text-annotation">Room capture</h2>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-annotation">
              {format(date, "EEE, MMM d, yyyy")}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDayOffset((o) => o - 1)}
              data-testid="capture-prev-day"
              aria-label="Previous day"
            >
              <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDayOffset(0)}
              data-testid="capture-today"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDayOffset((o) => o + 1)}
              data-testid="capture-next-day"
              aria-label="Next day"
            >
              Next
              <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
            </Button>
          </div>
        </div>

        <AsyncBoundary
          skeleton={<CaptureTimelineSkeleton />}
          fallback={(retry) => (
            <PanelError label="capture timeline" onRetry={retry} />
          )}
          resetKeys={[dateKey]}
        >
          <CaptureTimeline date={dateKey} isToday={isToday} />
        </AsyncBoundary>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-annotation-caps text-annotation">Capture nodes</h2>

        <AsyncBoundary
          skeleton={<CaptureNodesSkeleton />}
          fallback={(retry) => (
            <PanelError label="capture nodes" onRetry={retry} />
          )}
        >
          <CaptureNodesList />
        </AsyncBoundary>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-annotation-caps text-annotation">
          Flagged appointments
        </h2>

        <AsyncBoundary
          skeleton={<FlaggedVisitsSkeleton />}
          fallback={(retry) => (
            <PanelError label="flagged appointments" onRetry={retry} />
          )}
        >
          <FlaggedVisitsList />
        </AsyncBoundary>
      </section>
    </div>
  );
};

const CaptureTimelineSkeleton = () => (
  <SkeletonStack count={3} itemClassName="h-8" />
);

const CaptureNodesSkeleton = () => (
  <SkeletonStack count={2} itemClassName="h-10" />
);

const FlaggedVisitsSkeleton = () => (
  <SkeletonStack
    count={3}
    className="gap-3"
    itemClassName="h-24 border border-rule"
  />
);
